import { useState } from 'react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { useAuthStore } from '../../store/authStore';
import { toBnDigits } from '../../utils/bnFormat';
import { SEARCH_MODULES, isModuleAvailable } from './globalSearchModules';
import {
  IconKeyboard, IconSearch, IconArrowRight, IconArrowLeft, IconLayoutGrid,
  IconPin, IconArrowGuide, IconPrinter, IconFileTypePdf,
  IconDownload, IconPencil, IconCurrencyTaka,
  IconUpload, IconSquareCheck, IconSparkles, IconBolt,
} from '@tabler/icons-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';

export default function UserShortcutHelpModal({ open: controlledOpen, onOpenChange, showTrigger = true }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const opened = isControlled ? controlledOpen : internalOpen;
  const setOpened = isControlled ? onOpenChange : setInternalOpen;
  const { t, i18n } = useTranslation();
  const user = useAuthStore((st) => st.user);
  const isBdUser = !user?.country || String(user.country).toUpperCase() === 'BD';
  const isOwner = user?.role === 'OWNER';

  const rows = [
    { shortcut: 'Ctrl + K',           action: t('shortcuts.focusSearch'),        Icon: IconSearch },
    { shortcut: 'Ctrl + Shift + →',   action: t('shortcuts.next'),               Icon: IconArrowRight },
    { shortcut: 'Ctrl + Shift + ←',   action: t('shortcuts.previous'),           Icon: IconArrowLeft },
    { shortcut: 'Ctrl + Enter',        action: t('shortcuts.generateDocuments') + ' / ' + t('shortcuts.done'), Icon: IconArrowGuide },
    { shortcut: 'Ctrl + P',           action: t('shortcuts.print'),              Icon: IconPrinter },
    { shortcut: 'Ctrl + O',           action: t('shortcuts.openEditor'),         Icon: IconPencil },
    { shortcut: 'Ctrl + D',           action: t('shortcuts.downloadDocx'),       Icon: IconDownload },
    { shortcut: 'Ctrl + E',           action: t('shortcuts.downloadPdf'),        Icon: IconFileTypePdf },
    { shortcut: 'index + G',          action: t('shortcuts.openGenerateDrawer'), Icon: IconArrowGuide },
    { shortcut: 'index + P',          action: t('shortcuts.pinUnpin'),           Icon: IconPin },
    { shortcut: 'Ctrl + I',           action: t('shortcuts.openIncomeExpense'),  Icon: IconCurrencyTaka },
    // ── Open a module from any page: Alt + number, in the order of the home screen (the numbers live in globalSearchModules.js) ──
    { section: t('shortcuts.goToModuleSection') },
    ...SEARCH_MODULES
      .filter((m) => m.shortcut && isModuleAvailable(m, { isBdUser, isOwner }))
      .sort((a, b) => '1234567890'.indexOf(a.shortcut) - '1234567890'.indexOf(b.shortcut))
      .map((m) => ({ shortcut: `Alt + ${i18n.language === 'bn' ? toBnDigits(m.shortcut) : m.shortcut}`, action: t(m.labelKey), Icon: IconLayoutGrid })),
    // ── Bulk Photo Edit ──
    { section: t('shortcuts.bulkPhotoSection') },
    { shortcut: 'Ctrl + U',           action: t('shortcuts.bulkUpload'),         Icon: IconUpload },
    { shortcut: 'Ctrl + A',           action: t('shortcuts.bulkSelectAll'),      Icon: IconSquareCheck },
    { shortcut: '1 – 9',              action: t('shortcuts.bulkApplyPreset'),    Icon: IconSparkles },
    { shortcut: 'Ctrl + Enter',       action: t('shortcuts.bulkGenerate'),       Icon: IconBolt },
  ];

  return (
    <>
      <Dialog open={opened} onOpenChange={setOpened}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-2xl z-[999999] p-4 sm:p-6" overlayClassName="z-[999998]">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">{t('shortcuts.title')}</DialogTitle>
          </DialogHeader>
          <p className="text-xs sm:text-sm text-zinc-500 dark:text-zinc-400 -mt-2 mb-1.5">{t('shortcuts.tip')}</p>
          <hr className="border-zinc-200 dark:border-zinc-700" />
          <ScrollArea className="max-h-[65vh] overflow-y-auto">
            <table className="w-full text-xs sm:text-sm">
              <thead>
                <tr className="border-b border-zinc-200 dark:border-zinc-700">
                  <th className="text-left py-1.5 pr-3 w-[38%] font-medium text-zinc-700 dark:text-zinc-300">{t('shortcuts.shortcut')}</th>
                  <th className="text-left py-1.5 font-medium text-zinc-700 dark:text-zinc-300">{t('shortcuts.action')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) =>
                  row.section ? (
                    <tr key={i}>
                      <td colSpan={2} className="pt-3 pb-1.5">
                        <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                          {row.section}
                        </span>
                      </td>
                    </tr>
                  ) : (
                    <tr key={i} className={i % 2 === 0 ? 'bg-zinc-50 dark:bg-zinc-800/50' : ''}>
                      <td className="py-1.5 pr-3">
                        <code className="font-mono text-[11px] sm:text-xs text-zinc-700 dark:text-zinc-300 whitespace-nowrap">{row.shortcut}</code>
                      </td>
                      <td className="py-1.5">
                        <span className="inline-flex items-center gap-1 text-zinc-700 dark:text-zinc-300">
                          {row.Icon && <row.Icon size={12} className="shrink-0" />}
                          {row.action}
                        </span>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {showTrigger && (
        <button
          onClick={() => setOpened(true)}
          title={t('shortcuts.keyboardShortcuts')}
          aria-label={t('shortcuts.keyboardShortcuts')}
          className="inline-flex items-center justify-center w-8 h-8 rounded-md text-zinc-500 dark:text-zinc-400
            hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
        >
          <IconKeyboard size={16} />
        </button>
      )}
    </>
  );
}
